import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Zap, Building2, MapPin, Clock, Shield, Users, Home, Palette, Check, Star, Quote as QuoteIcon, ChevronLeft, ChevronRight, Minus, FileDown,
  Sun, Moon, Wifi, Smartphone, Ruler, Wrench, Truck, Leaf, Heart, ThumbsUp, Sparkles, Settings, Volume2, VolumeX, Thermometer, Eye, EyeOff, Lock, BadgeCheck, Hammer, Lightbulb, Layers, Package, Phone, Mail, Calendar, DollarSign, Percent, Gift, Timer, Factory, Scissors, Droplets, Wind, Baby, Dog, Flame,
  Mic, Cpu, LayoutGrid, Hexagon, Disc, SlidersHorizontal, Tablet, Monitor, Radio, Boxes, Circle, ToggleLeft } from 'lucide-react';

export const ICON_MAP = { Award, Zap, Building2, MapPin, Clock, Shield, Users, Home, Palette, Check, Star, Sun, Moon, Wifi, Smartphone, Ruler, Wrench, Truck, Leaf, Heart, ThumbsUp, Sparkles, Settings, Volume2, VolumeX, Thermometer, Eye, EyeOff, Lock, BadgeCheck, Hammer, Lightbulb, Layers, Package, Phone, Mail, Calendar, DollarSign, Percent, Gift, Timer, Factory, Scissors, Droplets, Wind, Baby, Dog, Flame,
  Mic, Cpu, LayoutGrid, Hexagon, Disc, SlidersHorizontal, Tablet, Monitor, Radio, Boxes, Circle, ToggleLeft };
export const ICON_NAMES = Object.keys(ICON_MAP);
import useEmblaCarousel from 'embla-carousel-react';
import { SectionShell, SectionHeader, isDarkBg, RichText } from '../shared';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';

const Icon = ({ name, className }) => {
  const C = (name && ICON_MAP[name]) || Award;
  return <C className={className} strokeWidth={1.5} />;
};

export const FeaturesSection = ({ section }) => {
  const p = section.props || {};
  const items = p.items || [];
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'icon_grid_4';

  if (layout === 'ticker') {
    const loop = [...items, ...items];
    return (
      <section id={section.style?.anchor || section.id} data-testid="section-features" data-section-id={section.id} className={`py-4 overflow-hidden ${dark || !section.style?.background ? 'bg-[color:var(--site-dark)]' : 'bg-[color:var(--site-surface)]'}`}>
        <div className="container-xl">
          <div className="flex items-center gap-6">
            {(p.eyebrow || p.title) && <span className="text-[color:var(--site-accent-light)] text-xs font-medium tracking-wider uppercase whitespace-nowrap flex-shrink-0">{p.eyebrow || p.title}</span>}
            <div className="flex-1 overflow-hidden relative">
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[color:var(--site-dark)] to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[color:var(--site-dark)] to-transparent z-10" />
              <motion.div className="flex gap-3" animate={{ x: [0, -1500] }} transition={{ x: { repeat: Infinity, repeatType: 'loop', duration: 30, ease: 'linear' } }}>
                {loop.map((it, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800/50 rounded-full hover:bg-neutral-700 transition-colors cursor-pointer group flex-shrink-0" title={it.title}>
                    <span className="text-white/80 group-hover:text-white transition-colors leading-6"><Icon name={it.icon} className="w-6 h-6" /></span>
                    <span className="text-white/80 text-xs font-medium group-hover:text-white whitespace-nowrap">{it.title}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'tiles') {
    return (
      <SectionShell section={{ ...section, style: { padding: 'compact', ...(section.style || {}) } }}>
        {(p.eyebrow || p.title) && (
          <div className="text-center mb-6">
            {p.eyebrow && <span className={`text-xs font-medium tracking-wider uppercase ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            {p.title && <h2 className={`mt-1 !text-xl !font-medium ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className={`p-4 rounded-lg text-center transition-colors ${dark ? 'bg-white/5 hover:bg-white/10' : 'bg-white border border-neutral-200'}`} data-testid={`feature-item-${i}`}>
              <div className="w-10 h-10 mx-auto mb-2 bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-900"><Icon name={it.icon} className="w-5 h-5" /></div>
              <h4 className={`!font-medium !text-sm mb-1 ${dark ? 'text-white' : 'text-neutral-900'}`}>{it.title}</h4>
              <p className={`text-xs leading-relaxed ${dark ? 'text-neutral-400' : 'text-neutral-500'}`}>{it.description}</p>
            </motion.div>
          ))}
        </div>
      </SectionShell>
    );
  }

  if (layout === 'cards_stacked') {
    return (
      <SectionShell section={{ ...section, style: { padding: 'compact', ...(section.style || {}) } }}>
        <SectionHeader props={p} style={{ align: section.style?.align }} dark={dark} className="mb-8" />
        <div className="grid md:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white p-5 rounded-lg border border-neutral-200 text-neutral-900" data-testid={`feature-item-${i}`}>
              <div className="w-10 h-10 flex items-center justify-center bg-neutral-100 rounded-lg border border-neutral-200 mb-3"><Icon name={it.icon} className="w-6 h-6 text-neutral-900" /></div>
              <h4 className="!font-medium !text-base mb-1">{it.title}</h4>
              <p className="text-neutral-500 text-sm leading-relaxed">{it.description}</p>
            </motion.div>
          ))}
        </div>
      </SectionShell>
    );
  }

  if (layout === 'bordered') {
    // Mirrors the coded HomePage "Who We Serve" cards.
    return (
      <SectionShell section={section}>
        <SectionHeader props={p} style={{ align: 'center' }} dark={dark} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className={`p-6 border transition-colors ${dark ? 'border-white/20 hover:border-white/50' : 'border-neutral-200 hover:border-neutral-400'}`} data-testid={`feature-item-${i}`}>
              <Icon name={it.icon} className="w-8 h-8 text-amber-700 mb-4" />
              <h4 className={`!text-lg !font-semibold mb-2 ${dark ? 'text-white' : 'text-neutral-900'}`}>{it.title}</h4>
              <p className={`text-sm ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{it.description}</p>
            </motion.div>
          ))}
        </div>
      </SectionShell>
    );
  }

  const cols = layout === 'icon_grid_3' || layout === 'cards' ? 'sm:grid-cols-2 lg:grid-cols-3' : layout === 'icon_grid_4' ? 'sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 max-w-3xl';
  const center = layout.startsWith('icon_grid');
  const iconBox = dark && layout !== 'cards' ? 'bg-white/10' : section.style?.background === 'light' ? 'bg-white border border-neutral-200' : 'bg-neutral-100';
  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={{ align: center ? 'center' : section.style?.align }} dark={dark} />
      <div className={`grid gap-8 ${cols}`}>
        {items.map((it, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.1 }}
            className={`${center ? 'text-center' : 'flex gap-5'} ${layout === 'cards' ? 'p-8 border border-neutral-200 bg-white rounded-[var(--site-radius)] text-neutral-900' : ''}`} data-testid={`feature-item-${i}`}>
            <div className={`flex-shrink-0 flex items-center justify-center ${center ? 'w-14 h-14 mx-auto mb-5' : 'w-12 h-12'} ${iconBox} rounded-[var(--site-radius)]`}>
              {layout === 'numbered' ? <span className="text-xl font-semibold text-[color:var(--site-accent)]">{i + 1}</span> : <Icon name={it.icon} className="w-6 h-6 text-[color:var(--site-accent)]" />}
            </div>
            <div>
              <h4 className={`!text-lg !font-semibold mb-2 ${dark && layout !== 'cards' ? 'text-white' : 'text-neutral-900'}`}>{it.title}</h4>
              <p className={`text-sm ${dark && layout !== 'cards' ? 'text-neutral-300' : 'text-neutral-600'}`}>{it.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </SectionShell>
  );
};

export const StatsSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  if (section.layout === 'band') {
    return (
      <SectionShell section={{ ...section, style: { background: 'brand', ...(section.style || {}), padding: section.style?.padding === 'normal' || !section.style?.padding ? 'compact' : section.style.padding } }}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {(p.items || []).map((s, i) => (
            <div key={i}><p className={`text-3xl font-bold mb-1 ${dark ? 'text-white' : 'text-neutral-900'}`}>{s.value}</p><p className={`text-sm ${dark ? 'text-white/80' : 'text-neutral-600'}`}>{s.label}</p></div>
          ))}
        </div>
      </SectionShell>
    );
  }
  return (
    <SectionShell section={section}>
      <SectionHeader props={{ title: p.title }} style={{ align: 'center' }} dark={dark} />
      <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(4, Math.max(2, (p.items || []).length))} gap-8`}>
        {(p.items || []).map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className={`text-center ${section.layout === 'cards' ? 'p-8 border border-neutral-200 bg-white rounded-[var(--site-radius)]' : ''}`}>
            <div className={`text-4xl lg:text-5xl font-semibold font-serif ${dark && section.layout !== 'cards' ? 'text-white' : 'text-neutral-900'}`}>{s.value}</div>
            <div className="mt-2 label-overline">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </SectionShell>
  );
};

export const LogosSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={{ ...section, style: { padding: 'compact', ...(section.style || {}) } }}>
      {p.title && <p className={`label-overline text-center mb-10 ${dark ? 'text-neutral-300' : ''}`}>{p.title}</p>}
      <div className={`${section.layout === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8' : 'flex flex-wrap items-center justify-center gap-10 lg:gap-16'}`}>
        {(p.items || []).map((l, i) => {
          const img = l.image?.url ? <img src={l.image.url} alt={l.image.alt_text || l.name} className={`h-10 lg:h-12 w-auto object-contain ${dark ? 'brightness-0 invert' : 'grayscale'} opacity-70 hover:opacity-100 hover:grayscale-0 transition-all`} loading="lazy" /> : <span className="text-lg font-semibold text-neutral-500">{l.name}</span>;
          return l.link ? <a key={i} href={l.link} target="_blank" rel="noreferrer" className="flex items-center justify-center">{img}</a> : <div key={i} className="flex items-center justify-center">{img}</div>;
        })}
      </div>
    </SectionShell>
  );
};

const Stars = ({ n }) => n ? <div className="flex gap-0.5 text-[color:var(--site-accent)]">{Array.from({ length: Math.min(5, n) }).map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div> : null;

const Quote = ({ t, dark, large }) => (
  <blockquote className={`${large ? 'text-center max-w-3xl mx-auto' : 'p-8 border border-neutral-200 bg-white rounded-[var(--site-radius)] h-full flex flex-col'} ${dark && !large ? 'text-neutral-900' : ''}`}>
    {large && <QuoteIcon className="w-10 h-10 mx-auto mb-6 text-[color:var(--site-accent)]" strokeWidth={1} />}
    <div className={large ? 'flex justify-center mb-4' : 'mb-4'}><Stars n={t.rating} /></div>
    <p className={`${large ? 'text-2xl lg:text-3xl font-serif leading-relaxed' : 'text-neutral-700 leading-relaxed flex-1'} ${large && dark ? 'text-white' : ''}`}>“{t.quote}”</p>
    <footer className={`mt-6 flex items-center gap-3 ${large ? 'justify-center' : ''}`}>
      {t.image?.url && <img src={t.image.url} alt={t.author} className="w-11 h-11 rounded-full object-cover" />}
      <div className={large ? 'text-center' : ''}><div className={`text-sm font-semibold ${large && dark ? 'text-white' : 'text-neutral-900'}`}>{t.author}</div>{t.role && <div className="text-xs text-neutral-500">{t.role}</div>}</div>
    </footer>
  </blockquote>
);

export const TestimonialsSection = ({ section }) => {
  const p = section.props || {};
  const items = p.items || [];
  const dark = isDarkBg(section.style);
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start' });
  const layout = section.layout || 'cards';
  return (
    <SectionShell section={section}>
      <SectionHeader props={{ eyebrow: p.eyebrow, title: p.title }} style={{ align: 'center' }} dark={dark} />
      {layout === 'single' ? items.slice(0, 1).map((t, i) => <Quote key={i} t={t} dark={dark} large />)
        : layout === 'carousel' ? (
          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef}><div className="flex gap-6">{items.map((t, i) => <div key={i} className="flex-[0_0_90%] md:flex-[0_0_48%] lg:flex-[0_0_32%]"><Quote t={t} dark={dark} /></div>)}</div></div>
            <div className="flex justify-center gap-3 mt-8">
              <button onClick={() => embla?.scrollPrev()} className="p-2 border border-neutral-300 hover:border-neutral-900 rounded-full" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => embla?.scrollNext()} className="p-2 border border-neutral-300 hover:border-neutral-900 rounded-full" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            </div>
          </div>
        ) : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((t, i) => <Quote key={i} t={t} dark={dark} />)}</div>}
    </SectionShell>
  );
};

export const FaqSection = ({ section }) => {
  const p = section.props || {};
  const items = [...(p.items || []), ...(section.data || [])].filter((q) => q.question);
  const dark = isDarkBg(section.style);
  const two = section.layout === 'two_column';
  return (
    <SectionShell section={section} container={two ? 'xl' : 'lg'}>
      <div className={two ? 'grid lg:grid-cols-[1fr_2fr] gap-12' : ''}>
        <SectionHeader props={{ eyebrow: p.eyebrow, title: p.title }} style={{ align: two ? 'left' : 'center' }} dark={dark} className={two ? 'mb-0' : ''} />
        <Accordion type="single" collapsible className={two ? 'w-full' : 'w-full space-y-4'} data-testid="faq-accordion">
          {items.map((q, i) => (
            <AccordionItem key={i} value={`q-${i}`} className={two ? (dark ? 'border-white/20' : '') : `border px-6 ${dark ? 'border-white/20' : 'border-neutral-200'}`}>
              <AccordionTrigger className={`text-left hover:no-underline ${two ? 'text-lg font-medium' : 'py-6'} ${dark ? 'text-white' : 'text-neutral-900'}`}>{two ? q.question : <span className="text-lg font-medium pr-8">{q.question}</span>}</AccordionTrigger>
              <AccordionContent className={`${dark ? 'text-neutral-300' : 'text-neutral-600'} ${two ? 'text-base' : 'pb-6 text-base'} leading-relaxed`}><RichText html={q.answer} /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      {items.length > 0 && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: items.map((q) => ({ '@type': 'Question', name: q.question, acceptedAnswer: { '@type': 'Answer', text: (q.answer || '').replace(/<[^>]+>/g, '') } })) }) }} />}
    </SectionShell>
  );
};

export const SpecsSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const rows = p.items || [];
  return (
    <SectionShell section={section} container="lg">
      <SectionHeader props={{ title: p.title }} dark={dark} />
      <dl className={`${section.layout === 'two_column' ? 'grid md:grid-cols-2 gap-x-12' : ''} divide-y divide-neutral-200 ${dark ? 'divide-white/20' : ''}`}>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr] gap-6 py-4">
            <dt className={`text-sm font-medium ${dark ? 'text-neutral-300' : 'text-neutral-500'}`}>{r.label}</dt>
            <dd className={`text-sm ${dark ? 'text-white' : 'text-neutral-900'}`}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </SectionShell>
  );
};

export const ComparisonSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const cols = (p.columns || '').split(',').map((c) => c.trim()).filter(Boolean);
  const rows = (p.items || []).map((r) => ({ label: r.label, values: (r.values || '').split(',').map((v) => v.trim()) }));
  const cell = (v) => v === 'yes' ? <Check className="w-5 h-5 text-[color:var(--site-accent)] mx-auto" /> : v === 'no' ? <Minus className="w-5 h-5 text-neutral-300 mx-auto" /> : v;
  return (
    <SectionShell section={section} container="lg">
      <SectionHeader props={{ title: p.title }} style={{ align: 'center' }} dark={dark} />
      <div className="overflow-x-auto">
        <table className={`w-full text-sm ${dark ? 'text-white' : 'text-neutral-900'}`} data-testid="comparison-table">
          <thead><tr className={`border-b ${dark ? 'border-white/20' : 'border-neutral-300'}`}><th className="text-left py-4 pr-4 font-medium text-neutral-500">Feature</th>{cols.map((c, i) => <th key={i} className="py-4 px-4 font-semibold text-center">{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className={`border-b ${dark ? 'border-white/10' : 'border-neutral-200'}`}><td className="py-4 pr-4 font-medium">{r.label}</td>{cols.map((_, j) => <td key={j} className="py-4 px-4 text-center">{cell(r.values[j] || '')}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </SectionShell>
  );
};

export const DownloadsSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section} container="lg">
      <SectionHeader props={{ title: p.title }} dark={dark} />
      <div className={section.layout === 'cards' ? 'grid sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'divide-y divide-neutral-200'}>
        {(p.items || []).map((d, i) => (
          <a key={i} href={d.file_url} target="_blank" rel="noreferrer" className={`flex items-start gap-4 group ${section.layout === 'cards' ? 'p-6 border border-neutral-200 bg-white rounded-[var(--site-radius)] hover:border-neutral-400' : 'py-5'}`}>
            <FileDown className="w-6 h-6 text-[color:var(--site-accent)] flex-shrink-0" strokeWidth={1.5} />
            <div><div className={`font-medium ${dark && section.layout !== 'cards' ? 'text-white' : 'text-neutral-900'} group-hover:text-[color:var(--site-accent)]`}>{d.title}</div>{d.description && <p className="text-sm text-neutral-500 mt-1">{d.description}</p>}</div>
          </a>
        ))}
      </div>
    </SectionShell>
  );
};
